import { Icons } from '@/utils/Icons';
import { useTranslation } from '@/i18n';
import { type DepositOption, type DepositOptionKind, formatRange } from './depositOptions';

type Props = {
  options: DepositOption[];
  isLoading: boolean;
  isError: boolean;
  onSelect: (option: DepositOption) => void;
};

/** GPay over PhonePe in one round badge, like the UPI apps logo. */
const UpiAppsIcon = () => (
  <span className="flex size-12 shrink-0 flex-col overflow-hidden rounded-full border-2 border-shell-border bg-surface">
    <span className="flex flex-1 items-end justify-center text-body">
      <Icons.googlePay className="size-6" />
    </span>
    <span className="flex flex-1 items-start justify-center bg-primary pt-0.5 text-body-inverse">
      <Icons.phonePe className="size-3.5" />
    </span>
  </span>
);

const ROUND_ICONS = {
  bank: { icon: Icons.bank, className: 'bg-gold/15 text-gold' },
  crypto: { icon: Icons.tether, className: 'bg-success-emerald text-body-inverse' },
};

export const DepositOptionIcon = ({ kind }: { kind: DepositOptionKind }) => {
  if (kind === 'upi') return <UpiAppsIcon />;
  const { icon: Icon, className } = ROUND_ICONS[kind];
  return (
    <span
      className={`flex size-12 shrink-0 items-center justify-center rounded-full border-2 border-shell-border ${className}`}
    >
      <Icon className="size-6" />
    </span>
  );
};

/** Step 2: one row per admin account (UPI, bank, crypto) whose limits accept the amount. */
const DepositMethodList = ({ options, isLoading, isError, onSelect }: Props) => {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <p className="py-8 text-center text-sm text-shell-muted">{t('deposit.loadingMethods')}</p>
    );
  }
  if (isError) {
    return <p className="py-8 text-center text-sm text-danger-red">{t('deposit.methodsError')}</p>;
  }
  if (!options.length) {
    return (
      <p className="py-8 text-center text-sm text-shell-muted">{t('deposit.noMethodForAmount')}</p>
    );
  }

  return (
    <ul className="space-y-3">
      {options.map((option) => {
        const range = formatRange(option.min, option.max);
        return (
          <li key={option.id}>
            <button
              type="button"
              onClick={() => onSelect(option)}
              className="flex min-h-[80px] w-full items-center justify-between gap-3 rounded-xl border border-transparent bg-shell-raised px-5 py-3 text-left transition hover:border-gold"
            >
              <span className="min-w-0">
                <span className="block break-words text-base text-shell-fg">{option.label}</span>
                {range && <span className="block text-xs text-shell-muted">{range}</span>}
              </span>
              <DepositOptionIcon kind={option.kind} />
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default DepositMethodList;
